// ── Comps (202609231600) ────────────────────────────────────────────────────
// What companies like this one sold for, and who bought them: our own won
// deals in the space (the only comps with a real multiple) and the market
// transactions Atlas learned from buyer portfolio pages + the press. The
// acquirers are one click from the blast tracker.
const compsMoney = (v) => (v == null ? '—' : v >= 1e6 ? `$${(v / 1e6 % 1 === 0 ? v / 1e6 : (v / 1e6).toFixed(1))}M` : `$${Math.round(v / 1e3)}k`);
const compsYear = (iso) => (iso ? String(iso).slice(0, 4) : '—');

// Shared (window.CompsSection, 2026-09-28): the deal page passes `deal`;
// the company and contact pages pass `companyId` — a prospect with no deal
// gets the same Market / Nearby / Ours / Acquirers, minus "Add to deal".
const CompsSection = ({ deal, companyId, sectionId = 'deal.comps' }) => {
  const dealId = deal && deal.id;
  const cid = deal ? deal.target : companyId;
  const sellerSide = deal ? deal.type !== 'buyer_buyside' : true;
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState('market');
  const [busy, setBusy] = React.useState(null);
  const [sweeping, setSweeping] = React.useState(false);
  const load = React.useCallback(() => {
    if (!sellerSide || !window.AtlasApi.dealComps) return;
    const req = dealId ? window.AtlasApi.dealComps(dealId) : (cid && window.AtlasApi.companyComps ? window.AtlasApi.companyComps(cid) : null);
    if (!req) return;
    req.then((r) => setData(r || null)).catch(() => setData({ own: [], market: [], acquirers: [], stats: {} }));
  }, [dealId, cid, sellerSide]);
  React.useEffect(() => { load(); }, [load]);
  if (!sellerSide || !window.AtlasApi.dealComps || (!dealId && !cid)) return null;
  const own = (data && data.own) || [];
  const market = (data && data.market) || [];
  const acquirers = (data && data.acquirers) || [];
  const nearby = (data && data.nearby) || [];
  const stats = (data && data.stats) || {};
  // Trade-and-region sweep on demand: search the web for sales in this
  // seller's trade and state, file what Atlas has, keep the rest, reload.
  const sweepNearby = async () => {
    if (!cid) return;
    setSweeping(true);
    try {
      const r = await window.AtlasApi.compsNearbySweep(cid);
      const sw = (r && r.swept) || {};
      pushToast({ type: 'success', title: `Web sweep: ${sw.found || 0} sale${sw.found === 1 ? '' : 's'} found in ${sw.industry || 'the trade'} · ${sw.state || ''}`,
                  body: `${sw.matched || 0} already Atlas companies` });
      load();
    } catch (e) { pushToast({ type: 'error', title: 'Sweep failed', body: String(e.message || e) }); }
    finally { setSweeping(false); }
  };
  const addToDeal = async (a) => {
    setBusy(a.buyer_id);
    try {
      const r = await window.AtlasApi.sendBuyerDeal(a.buyer_id, { engagement_id: dealId, note: `Bought in this space: ${(a.targets || []).join(', ')}` });
      pushToast(r.added ? { type: 'success', title: `${a.firm} added to the deal` } : { type: 'info', title: `${a.firm} is already on this deal` });
      load();
    } catch (e) { pushToast({ type: 'error', title: 'Could not add them', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const TabBtn = ({ k, label, n }) => (
    <button onClick={() => setTab(k)} style={{
      padding: '2px 8px', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11,
      background: tab === k ? 'var(--surface-2)' : 'transparent', fontWeight: tab === k ? 600 : 500,
      color: tab === k ? 'var(--text)' : 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4,
      ...((window.AtlasDevice && window.AtlasDevice.phone) ? { minHeight: 44, padding: '0 12px', fontSize: 13 } : null) }}>
      {label}<span style={{ fontSize: 10, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
    </button>
  );
  // The Market / Nearby / Ours / Acquirers strip is ~276px — wider than a
  // phone header has room for. Phone: it moves to the top of the body and
  // wraps. Other touch screens: it stays in the header but scrolls sideways
  // inside the card (capped at the header's action slot, which is capped at
  // the row; flex-start, or the overflow would sit off its left edge where it
  // can't be scrolled to). Desktop: the header wraps it.
  const cDev = window.AtlasDevice || {};
  const stripInBody = !!cDev.phone;
  const stripScrolls = !cDev.phone && !!cDev.touch;
  const action = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-4)',
                   ...(stripInBody ? { flexWrap: 'wrap', rowGap: 4 } : null),
                   ...(stripScrolls ? { flexWrap: 'nowrap', justifyContent: 'flex-start', maxWidth: '100%', overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' } : null) }}>
      {stats.median_multiple && (
        <span title={`Median of ${stats.priced} comp${stats.priced === 1 ? '' : 's'} with a disclosed price and EBITDA`}>
          median {stats.median_multiple}× EBITDA{stats.implied_value_usd ? ` → ~${compsMoney(stats.implied_value_usd)} here` : ''}
        </span>
      )}
      <TabBtn k="market" label="Market" n={market.length} />
      <TabBtn k="nearby" label="Nearby" n={nearby.length} />
      <TabBtn k="own" label="Ours" n={own.length} />
      <TabBtn k="acquirers" label="Acquirers" n={acquirers.length} />
    </span>
  );
  const sub = { fontSize: 11, color: 'var(--text-4)', marginTop: 2 };
  const row = { padding: '8px 0', borderBottom: '1px solid var(--border)' };
  return (
    <Section title="Comps" action={stripInBody ? null : action} collapsible id={sectionId} defaultOpen={false}
      preview={data ? `${market.length} market · ${nearby.length} nearby · ${own.length} ours · ${acquirers.length} acquirer${acquirers.length === 1 ? '' : 's'}${stats.median_multiple ? ` · median ${stats.median_multiple}× EBITDA` : ''}` : 'Loading…'}>
      <div style={{ padding: '6px 14px 10px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' }}>
        {stripInBody && <div style={{ paddingBottom: 6 }}>{action}</div>}
        {data === null && <div style={{ color: 'var(--text-3)', padding: '6px 0' }}>Loading…</div>}
        {data && tab === 'market' && !market.length && (
          <div style={{ color: 'var(--text-3)', padding: '6px 0' }}>
            No market transactions in {data.seller?.industry || 'this space'} yet.
          </div>
        )}
        {data && tab === 'market' && market.map((m) => (
          <div key={m.id} style={row}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => window.AtlasNav?.goToCompany?.(m.company_id)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                {m.company}
              </button>
              <span style={{ color: 'var(--text-4)' }}>→</span>
              {m.buyer_id ? (
                <button onClick={() => window.AtlasNav?.goToBuyer?.(m.buyer_id)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                  {m.acquirer}
                </button>
              ) : <span>{m.acquirer}</span>}
              {m.deal_type && m.deal_type !== 'unknown' && <Pill hue={m.deal_type === 'platform' ? 'violet' : 'blue'} sz="sm">{m.deal_type.replace('_', '-')}</Pill>}
              {m.multiple && <Pill hue="green" sz="sm">{m.multiple}×</Pill>}
              {m.distance_mi != null && (
                <Pill hue={m.distance_mi <= 50 ? 'green' : 'slate'} sz="sm" title={`${m.distance_mi} miles from the seller's HQ`}>{m.distance_mi} mi</Pill>
              )}
              {m.distance_mi == null && m.same_state && <Pill hue="slate" sz="sm">same state</Pill>}
              {!m.same_industry && <Pill hue="slate" sz="sm" title="Matched on keywords, not the Atlas industry">adjacent</Pill>}
            </div>
            <div style={sub}>
              {[compsYear(m.announced), m.price_text || (m.price_usd ? compsMoney(m.price_usd) : 'price undisclosed'),
                m.seller_ebitda_usd && `${compsMoney(m.seller_ebitda_usd)} EBITDA`, m.seller_revenue_usd && `${compsMoney(m.seller_revenue_usd)} revenue`,
                m.industry, m.state].filter(Boolean).join(' · ')}
              {m.url && <> · <a href={m.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>source</a></>}
            </div>
            {m.summary && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{m.summary}</div>}
          </div>
        ))}
        {data && tab === 'nearby' && !nearby.length && (
          <div style={{ color: 'var(--text-3)', padding: '6px 0' }}>
            {data.seller?.industry
              ? <>Nothing in {data.seller.industry} sold within {data.nearby_radius_mi || 200} miles of {data.seller.company} that we know of — or its HQ city is not on file.</>
              : <>Set the company's industry to see what sold nearby in its trade.</>}
          </div>
        )}
        {data && tab === 'nearby' && nearby.length > 0 && (
          <div style={{ color: 'var(--text-4)', fontSize: 11, padding: '4px 0 2px' }}>
            {data.seller?.industry || 'Same trade'} within {data.nearby_radius_mi || 50} miles of the seller, nearest first
            {data.nearby_widened ? ' — nothing in the trade within 50, so the radius was widened' : ''}.
          </div>
        )}
        {data && tab === 'nearby' && window.AtlasApi.compsNearbySweep && (
          <div style={{ padding: '4px 0 6px' }}>
            <Btn size="xs" variant="outline" disabled={sweeping} onClick={sweepNearby}
              title="Search the web for sales in this trade and state">
              {sweeping ? 'Searching the web…' : 'Search the web for more'}
            </Btn>
          </div>
        )}
        {data && tab === 'nearby' && nearby.map((n) => (
          <div key={`${n.source}-${n.id}`} style={row}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => window.AtlasNav?.goToCompany?.(n.company_id)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                {n.company}
              </button>
              <span style={{ color: 'var(--text-4)' }}>→</span>
              {n.source === 'ours' ? (
                <button onClick={() => window.AtlasNav?.goToDeal?.(n.engagement_id)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                  sold by IndustryPro
                </button>
              ) : n.buyer_id ? (
                <button onClick={() => window.AtlasNav?.goToBuyer?.(n.buyer_id)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                  {n.acquirer}
                </button>
              ) : <span style={{ color: 'var(--text-2)' }}>{n.acquirer || 'acquired'}</span>}
              <Pill hue={n.distance_mi <= 15 ? 'green' : 'slate'} sz="sm">{n.distance_mi} mi</Pill>
              {n.match === 'keywords' && <Pill hue="slate" sz="sm" title="Matched on the seller's own words, not the Atlas industry">related trade</Pill>}
              {n.source === 'web' && <Pill hue="slate" sz="sm" title="Found on the web by the trade sweep; not an Atlas company">web</Pill>}
            </div>
            <div style={sub}>
              {[compsYear(n.when), n.industry, [n.city, n.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
              {n.url && <> · <a href={n.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>source</a></>}
            </div>
            {n.summary && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{n.summary}</div>}
          </div>
        ))}
        {data && tab === 'own' && !own.length && (
          <div style={{ color: 'var(--text-3)', padding: '6px 0' }}>No closed IndustryPro deals in this space yet.</div>
        )}
        {data && tab === 'own' && own.map((o) => (
          <div key={o.engagement_id} style={row}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => window.AtlasNav?.goToDeal?.(o.engagement_id)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                {o.company || o.deal}
              </button>
              {o.multiple && <Pill hue="green" sz="sm">{o.multiple}×</Pill>}
              {!o.same_industry && <Pill hue="slate" sz="sm" title="Matched on keywords, not the Atlas industry">adjacent</Pill>}
            </div>
            <div style={sub}>
              {[compsYear(o.closed_on), o.size_usd ? `${compsMoney(o.size_usd)} deal` : 'size not recorded',
                o.ebitda_usd && `${compsMoney(o.ebitda_usd)} EBITDA`, o.revenue_usd && `${compsMoney(o.revenue_usd)} revenue`,
                o.industry, o.state, o.dealmaker].filter(Boolean).join(' · ')}
            </div>
          </div>
        ))}
        {data && tab === 'acquirers' && !acquirers.length && (
          <div style={{ color: 'var(--text-3)', padding: '6px 0' }}>No roster buyer has a confirmed acquisition in this space yet.</div>
        )}
        {data && tab === 'acquirers' && acquirers.map((a) => (
          <div key={a.buyer_id} style={{ ...row, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <button onClick={() => window.AtlasNav?.goToBuyer?.(a.buyer_id)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
                {a.firm}
              </button>
              <div style={sub}>bought {a.n === 1 ? a.targets[0] : `${a.n} in this space (${a.targets.join(', ')})`}{a.last ? ` · last ${a.last}` : ''}</div>
            </div>
            {a.on_deal
              ? <Pill hue="green" sz="sm">on this deal</Pill>
              : dealId ? <Btn size="xs" variant="outline" disabled={busy === a.buyer_id} onClick={() => addToDeal(a)}>Add to deal</Btn> : null}
          </div>
        ))}
      </div>
    </Section>
  );
};

window.CompsSection = CompsSection;

// ── Market card + workspace (record-page redesign step 6) ─────────────────────
// v2 only: the page slots swap CompsSection for MarketCard
// (window.recordCardsOn('MarketCard')), and ?panel=market opens MarketWorkspace in
// the page's PanelHost. CompsSection above stays for classic and the phones.
const COMPS_NEAREST_KEYS = ['source', 'id', 'company_id', 'engagement_id', 'company', 'city', 'state', 'distance_mi',
  'when', 'acquirer', 'same_industry'];
const compsHas = (o, k) => o != null && Object.prototype.hasOwnProperty.call(o, k);
const compsGet = (o, k) => (compsHas(o, k) && o[k] !== undefined ? o[k] : null);
// The Market card's numbers from a comps payload: the shape the backend caches
// for the record summary (routes/comps.py), pinned equal by
// atlas-crm/test/fixtures/comps_card_stats.json.
const compsCardStats = (payload) => {
  const out = payload || {};
  const st = out.stats || {};
  const near = Array.isArray(out.nearby) && out.nearby.length ? out.nearby[0] : null;
  const nearest = near && Object.keys(near).length
    ? Object.fromEntries(COMPS_NEAREST_KEYS.map((k) => [k, compsGet(near, k)])) : null;
  const n = (k) => (compsHas(st, k) ? st[k] : 0);
  return {
    median_multiple: compsGet(st, 'median_multiple'),
    implied_value_usd: compsGet(st, 'implied_value_usd'),
    priced: n('priced'), own: n('own'), market: n('market'), nearby: n('nearby'),
    acquirers: (out.acquirers || []).length,
    nearest_sale: nearest,
    nearby_radius_mi: compsGet(out, 'nearby_radius_mi'),
    as_of: compsGet(out, 'as_of'),
  };
};
// A summary speaks for company `cid` once it has caught up with a re-linked
// deal or person (deal and contact summaries carry their company_id; a
// company's own summary has none and is always its own).
const compsSummaryHere = (s, cid) => !!s && (s.company_id == null || String(s.company_id) === String(cid));
// The card's numbers for company `cid`: the summary's cached comps when it
// speaks for that company, else the card's cold read ({ cid, stats }) when it
// was made for that company; null otherwise (never another company's).
const compsCardNumbers = (s, cid, cold) => (compsSummaryHere(s, cid) && s.comps)
  || (cold && cold.cid === cid ? cold.stats : null);
// "6.8× → ~$193.1M" ('' without a median).
const compsImplied = (st) => (st && st.median_multiple
  ? `${st.median_multiple}×${st.implied_value_usd ? ` → ~${compsMoney(st.implied_value_usd)}` : ''}` : '');
// "Median 6.8× EBITDA → ~$193.1M" ('' without a median).
const compsMedianLine = (st) => (st && st.median_multiple
  ? `Median ${st.median_multiple}× EBITDA${st.implied_value_usd ? ` → ~${compsMoney(st.implied_value_usd)}` : ''}` : '');
// The HelpTip on the median: the method, nothing else.
const compsMethod = (st, whose) => `The median EV / EBITDA of the ${(st && st.priced) || 0} comps with a disclosed price and EBITDA, times ${whose} EBITDA.`;
// The folded line: "6.8× → ~$193.1M · Badger Provisions sold 12 mi"; missing parts drop; nothing is —.
const compsPreviewText = (st) => {
  if (!st) return '—';
  const near = st.nearest_sale;
  const parts = [compsImplied(st),
    near && near.company ? `${near.company} sold${near.distance_mi != null ? ` ${near.distance_mi} mi` : ''}` : ''];
  return parts.filter(Boolean).join(' · ') || '—';
};
// Client-side sort per workspace tab; nulls sink both ways (table_helpers makeComparator's rule).
const compsLower = (v) => (v ? String(v).toLowerCase() : null);
const compsYearNum = (iso) => (iso ? (Number(String(iso).slice(0, 4)) || null) : null);
const COMPS_SORT = Object.freeze({
  market: { company: (r) => compsLower(r.company), acquirer: (r) => compsLower(r.acquirer), industry: (r) => compsLower(r.industry),
    multiple: (r) => r.multiple ?? null, price: (r) => r.price_usd ?? null, ebitda: (r) => r.seller_ebitda_usd ?? null,
    revenue: (r) => r.seller_revenue_usd ?? null, distance: (r) => r.distance_mi ?? null, year: (r) => compsYearNum(r.announced) },
  nearby: { company: (r) => compsLower(r.company), acquirer: (r) => compsLower(r.acquirer), distance: (r) => r.distance_mi ?? null,
    trade: (r) => compsLower(r.industry), year: (r) => compsYearNum(r.when) },
  ours: { company: (r) => compsLower(r.company || r.deal), multiple: (r) => r.multiple ?? null, size: (r) => r.size_usd ?? null,
    ebitda: (r) => r.ebitda_usd ?? null, revenue: (r) => r.revenue_usd ?? null, closed: (r) => r.closed_on || null,
    industry: (r) => compsLower(r.industry), dealmaker: (r) => compsLower(r.dealmaker) },
  acquirers: { firm: (r) => compsLower(r.firm), n: (r) => r.n ?? null, last: (r) => r.last ?? null },
});
const compsSortRows = (tab, rows, sort) => {
  const ex = sort && COMPS_SORT[tab] && COMPS_SORT[tab][sort.key];
  if (!ex) return rows || [];
  const sign = sort.dir === 'desc' ? -1 : 1;
  return (rows || []).slice().sort((a, b) => {
    const x = ex(a); const y = ex(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x < y ? -sign : x > y ? sign : 0;
  });
};
// URL tab → label → payload list ('ours' is the payload's `own`).
const MARKET_TABS = Object.freeze([['market', 'Market', 'market'], ['nearby', 'Nearby', 'nearby'], ['ours', 'Ours', 'own'], ['acquirers', 'Acquirers', 'acquirers']]);
const marketTabOf = (t) => (MARKET_TABS.some(([k]) => k === t) ? t : 'market');
window.CompsKit = Object.freeze({ cardStats: compsCardStats, implied: compsImplied, medianLine: compsMedianLine,
  method: compsMethod, previewText: compsPreviewText, money: compsMoney, sortRows: compsSortRows, SORT: COMPS_SORT,
  TABS: MARKET_TABS, tabOf: marketTabOf, summaryHere: compsSummaryHere, cardNumbers: compsCardNumbers });

const MARKET_CHIP = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--text-2)', padding: '1px 8px',
  border: '1px solid var(--border)', borderRadius: 999, whiteSpace: 'nowrap', background: 'none', cursor: 'pointer', font: 'inherit' };
const MARKET_DIM = { color: 'var(--text-4)' };
const MARKET_LINK = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'var(--accent-text)' };
const openMarket = (tab) => window.openRecordPanel('market', { tab }, { via: 'button' });

const MarketCard = ({ deal, companyId, sectionId = 'deal.comps', variant = 'card' }) => {
  const kind = deal ? 'deal' : 'company';
  const rid = deal ? deal.id : companyId;
  const cid = deal ? deal.target : companyId;
  const applies = !!rid && !window.AtlasIsGuest && !(deal && (deal.type === 'buyer_buyside' || !deal.target));
  const sum = window.useRecordSummary(kind, rid, { enabled: applies });
  const [cold, setCold] = React.useState(null);     // { cid, stats }: the card's own read, for that company only
  const asked = React.useRef(null);                 // the company the cold read was made for
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // A cold summary cache (comps null): read the comps once per company, after
  // first paint. That default-sized GET also warms the cache the next summary
  // reads. A deal re-pointed at another company reads again for that one.
  const needCold = applies && !sum.loading && compsSummaryHere(sum.data, cid) && sum.data.comps == null;
  React.useEffect(() => {
    if (!needCold || asked.current === cid) return;
    asked.current = cid;
    const req = deal ? window.AtlasApi.dealComps(deal.id) : window.AtlasApi.companyComps(companyId);
    Promise.resolve(req).then((p) => { if (alive.current && p) setCold({ cid, stats: compsCardStats(p) }); }).catch(() => {});
  }, [needCold, cid]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!applies) return null;
  const st = compsCardNumbers(sum.data, cid, cold);
  const near = st && st.nearest_sale;
  const line = compsMedianLine(st);
  const expand = window.recordPanelAvailable('market', window.recordSurface()) ? (
    <window.Btn size="xs" variant="ghost" title="Open" icon={<window.Icon.Maximize size={11} />} onClick={() => openMarket('market')} />
  ) : null;
  const chips = [['market', 'Market', st && st.market], ['nearby', 'Nearby', st && st.nearby],
    ['ours', 'Ours', st && st.own], ['acquirers', 'Acquirers', st && st.acquirers]];
  return (
    <window.Section variant={variant} title="Market" action={expand}
      collapsible id={sectionId} defaultOpen={false} preview={sum.loading ? '—' : compsPreviewText(st)}>
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {line ? <span>{line}</span> : <span style={MARKET_DIM}>Median —</span>}
          <window.HelpTip text={compsMethod(st, deal ? "this deal's" : "the company's")} />
        </div>
        {near && near.company ? (
          <button type="button" style={MARKET_LINK} onClick={() => openMarket('nearby')}>
            {[`${near.company} sold`, near.distance_mi != null ? `${near.distance_mi} mi` : null, near.when ? compsYear(near.when) : null].filter(Boolean).join(' · ')}
          </button>
        ) : <span style={MARKET_DIM}>Nearest sale —</span>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {chips.map(([k, label, n]) => (
            <button key={k} type="button" style={MARKET_CHIP} onClick={() => openMarket(k)}>
              {label}<span style={{ color: n ? 'var(--text-2)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n == null ? '—' : n}</span>
            </button>
          ))}
        </div>
      </div>
    </window.Section>
  );
};

const EMPTY_COMPS = Object.freeze({ own: [], market: [], acquirers: [], nearby: [], stats: {} });
const MW_CELL = { padding: '6px 10px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' };
const mwDim = (v) => (v == null || v === '' ? <span style={MARKET_DIM}>—</span> : v);
const mwMoney = (v) => (v == null ? <span style={MARKET_DIM}>—</span> : compsMoney(v));
const mwSource = (url) => (url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>source</a> : mwDim(null));
// The company link with the deal summary under it (two lines; the whole text on hover).
const mwWho = (link, summary) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</div>
    {summary ? (
      <div title={summary} style={{ fontSize: 11.5, lineHeight: 1.4, color: 'var(--text-3)', display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }}>{summary}</div>
    ) : null}
  </div>
);
// The Atlas industry, and 'adjacent' when the comp matched on keywords instead (it still feeds the median).
const mwIndustry = (r) => (
  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', minWidth: 0 }}>{mwDim(r.industry)}
    {!r.same_industry && <window.Pill hue="slate" sz="sm" title="Matched on keywords, not the Atlas industry">adjacent</window.Pill>}</span>
);
const mwPlace = (r) => mwDim([r.city, r.state].filter(Boolean).join(', '));
// The market tab carries the most columns: it scrolls sideways below this width.
const MW_MIN_WIDTH = { market: 1180 };

// One full-width table: SortableHeader cells (table_helpers.jsx), flex rows; an empty table is one dimmed —.
const CompsTable = ({ cols, rows, sort, onSort, rowKey, minWidth = 880 }) => (
  <div style={{ minWidth }}>
    <div style={{ display: 'flex', height: 32, position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      {cols.map((c, i) => (
        <window.SortableHeader key={c.key} col={{ ...c, last: i === cols.length - 1 }}
          sortKey={sort && sort.key} sortDir={sort && sort.dir} onSort={c.sort ? onSort : null} sortable={!!c.sort} />
      ))}
    </div>
    {rows.length ? rows.map((r, i) => (
      <div key={rowKey(r, i)} data-market-row="" style={{ display: 'flex', alignItems: 'center', minHeight: 36, borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
        {cols.map((c) => <div key={c.key} style={{ ...MW_CELL, width: c.w, flex: c.flex, flexShrink: c.w ? 0 : 1, textAlign: c.align || 'left' }}>{c.cell(r)}</div>)}
      </div>
    )) : <div style={{ padding: 10, color: 'var(--text-4)' }}>—</div>}
  </div>
);

const MarketWorkspace = ({ api }) => {
  const kind = api.rec.kind;
  const ctx = api.ctx || {};
  const dealId = kind === 'deal' ? api.rec.id : null;
  const cid = kind === 'company' ? api.rec.id : (ctx.companyId || null);
  const canWrite = !!api.canWrite;
  const tab = marketTabOf(api.params && api.params.tab);
  const [data, setData] = React.useState(null);
  const [sweeping, setSweeping] = React.useState(false);
  const [adding, setAdding] = React.useState(null);  // the acquirer row whose add-buyer drawer is open
  const [sorts, setSorts] = React.useState({});      // per tab: { key, dir }
  const seq = React.useRef(0);
  const load = React.useCallback(() => {
    const req = dealId ? window.AtlasApi.dealComps(dealId) : (cid ? window.AtlasApi.companyComps(cid) : null);
    if (!req) { setData(EMPTY_COMPS); return; }
    const n = ++seq.current;
    req.then((r) => { if (n === seq.current) setData(r || EMPTY_COMPS); })
      .catch(() => { if (n === seq.current) setData((d) => d || EMPTY_COMPS); });
  }, [dealId, cid]);
  // Once on first open, and again on every reopen (active false → true): the stat is always live here.
  React.useEffect(() => { if (api.active) load(); }, [api.active, load]);
  // A hidden workspace keeps no drawer of its own open: closing Market closes the add drawer.
  React.useEffect(() => { if (!api.active) setAdding(null); }, [api.active]);
  const toggleSort = (key) => setSorts((m) => {
    const cur = m[tab] || {};
    return { ...m, [tab]: cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' } };
  });
  const sweep = async () => {
    if (!cid) return;
    setSweeping(true);
    try {
      const r = await window.AtlasApi.compsNearbySweep(cid);
      const sw = (r && r.swept) || {};
      window.pushToast({ type: 'success', title: `Web sweep: ${sw.found || 0} sale${sw.found === 1 ? '' : 's'} found` });
      load();
    } catch (e) { window.pushToast({ type: 'error', title: 'Sweep failed', body: String((e && e.message) || e) }); }
    finally { setSweeping(false); }
  };
  // Add to deal = the deal's add-buyer flow (views/buyer_add.jsx → POST /api/buyers/add-to-deal →
  // app/buyer_add.py: roster row + pursuit record), firm pre-picked. The drawer toasts; "Open Buyers"
  // rides on that toast only where the Buyers workspace exists (step 5), else the toast has no action.
  const openBuyers = dealId && window.recordPanelAvailable('buyers', window.recordSurface())
    ? { label: 'Open Buyers', onClick: () => window.AtlasNav.goToDeal(dealId, { panel: 'buyers', tab: 'board' }) } : undefined;
  const onAdded = () => {
    setAdding(null);
    if (window.notifyBuyersChanged) window.notifyBuyersChanged(dealId);
    load();
  };
  const co = ctx.company || null;
  const name = kind === 'deal' ? ((api.record && api.record.targetName) || (co && co.name) || '')
    : kind === 'company' ? ((api.record && api.record.name) || '')
    : ((co && co.name) || (api.record && api.record._raw && api.record._raw.company_name) || '');
  const st = data ? compsCardStats(data) : null;
  const line = compsMedianLine(st);
  const list = MARKET_TABS.find(([k]) => k === tab)[2];
  const rows = compsSortRows(tab, (data && data[list]) || [], sorts[tab]);
  const toCompany = (id, label) => (id
    ? <button type="button" style={{ ...MARKET_LINK, color: 'var(--text)', fontWeight: 600 }} onClick={() => window.AtlasNav.goToCompany(id)}>{label}</button>
    : <span style={{ fontWeight: 600 }}>{label}</span>);
  const toBuyer = (id, label) => (id
    ? <button type="button" style={MARKET_LINK} onClick={() => window.AtlasNav.goToBuyer(id)}>{label}</button> : mwDim(label));
  const COLS = {
    market: [
      { key: 'company', label: 'Company', flex: 1.5, sort: true, cell: (m) => mwWho(toCompany(m.company_id, m.company), m.summary) },
      { key: 'acquirer', label: 'Acquirer', flex: 1.1, sort: true, cell: (m) => toBuyer(m.buyer_id, m.acquirer) },
      { key: 'industry', label: 'Industry', flex: 1, sort: true, cell: mwIndustry },
      { key: 'where', label: 'Location', flex: 0.8, cell: mwPlace },
      { key: 'type', label: 'Type', w: 96, cell: (m) => (m.deal_type && m.deal_type !== 'unknown'
        ? <window.Pill hue={m.deal_type === 'platform' ? 'violet' : 'blue'} sz="sm">{m.deal_type.replace('_', '-')}</window.Pill> : mwDim(null)) },
      { key: 'multiple', label: 'Multiple', w: 84, align: 'right', sort: true, cell: (m) => (m.multiple ? `${m.multiple}×` : mwDim(null)) },
      { key: 'price', label: 'Price', w: 92, align: 'right', sort: true, cell: (m) => (m.price_usd ? compsMoney(m.price_usd) : mwDim(m.price_text || null)) },
      { key: 'ebitda', label: 'EBITDA', w: 92, align: 'right', sort: true, cell: (m) => mwMoney(m.seller_ebitda_usd) },
      { key: 'revenue', label: 'Revenue', w: 92, align: 'right', sort: true, cell: (m) => mwMoney(m.seller_revenue_usd) },
      { key: 'distance', label: 'Distance', w: 96, align: 'right', sort: true, cell: (m) => (m.distance_mi != null ? `${m.distance_mi} mi`
        : m.same_state ? <window.Pill hue="slate" sz="sm">same state</window.Pill> : mwDim(null)) },
      { key: 'year', label: 'Year', w: 64, sort: true, cell: (m) => compsYear(m.announced) },
      { key: 'source', label: 'Source', w: 70, cell: (m) => mwSource(m.url) },
    ],
    nearby: [
      { key: 'company', label: 'Company', flex: 1.4, sort: true, cell: (n) => mwWho(toCompany(n.company_id, n.company), n.summary) },
      { key: 'acquirer', label: 'Acquirer', flex: 1.2, sort: true, cell: (n) => (n.source === 'ours'
        ? <button type="button" style={MARKET_LINK} onClick={() => window.AtlasNav.goToDeal(n.engagement_id)}>IndustryPro</button>
        : toBuyer(n.buyer_id, n.acquirer)) },
      { key: 'distance', label: 'Distance', w: 84, align: 'right', sort: true, cell: (n) => (n.distance_mi != null ? `${n.distance_mi} mi` : mwDim(null)) },
      { key: 'trade', label: 'Trade', flex: 1, sort: true, cell: (n) => (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>{mwDim(n.industry)}
          {n.match === 'keywords' && <window.Pill hue="slate" sz="sm">related trade</window.Pill>}
          {n.source === 'web' && <window.Pill hue="slate" sz="sm">web</window.Pill>}</span>) },
      { key: 'where', label: 'Location', flex: 1, cell: (n) => mwDim([n.city, n.state].filter(Boolean).join(', ')) },
      { key: 'year', label: 'Year', w: 64, sort: true, cell: (n) => compsYear(n.when) },
      { key: 'source', label: 'Source', w: 70, cell: (n) => mwSource(n.url) },
    ],
    ours: [
      { key: 'company', label: 'Company', flex: 1.4, sort: true, cell: (o) => (
        <button type="button" style={{ ...MARKET_LINK, fontWeight: 600 }} onClick={() => window.AtlasNav.goToDeal(o.engagement_id)}>{o.company || o.deal}</button>) },
      { key: 'multiple', label: 'Multiple', w: 84, align: 'right', sort: true, cell: (o) => (o.multiple ? `${o.multiple}×` : mwDim(null)) },
      { key: 'size', label: 'Size', w: 92, align: 'right', sort: true, cell: (o) => mwMoney(o.size_usd) },
      { key: 'ebitda', label: 'EBITDA', w: 92, align: 'right', sort: true, cell: (o) => mwMoney(o.ebitda_usd) },
      { key: 'revenue', label: 'Revenue', w: 92, align: 'right', sort: true, cell: (o) => mwMoney(o.revenue_usd) },
      { key: 'closed', label: 'Closed', w: 70, sort: true, cell: (o) => compsYear(o.closed_on) },
      { key: 'industry', label: 'Industry', flex: 1, sort: true, cell: mwIndustry },
      { key: 'state', label: 'State', w: 90, cell: (o) => mwDim(o.state) },
      { key: 'dealmaker', label: 'Dealmaker', flex: 1, sort: true, cell: (o) => mwDim(o.dealmaker) },
    ],
    acquirers: [
      { key: 'firm', label: 'Firm', flex: 1.4, sort: true, cell: (a) => toBuyer(a.buyer_id, a.firm) },
      { key: 'n', label: 'Deals', w: 70, align: 'right', sort: true, cell: (a) => a.n },
      { key: 'targets', label: 'Bought', flex: 2, cell: (a) => mwDim((a.targets || []).join(', ')) },
      { key: 'last', label: 'Last', w: 64, sort: true, cell: (a) => mwDim(a.last) },
      { key: 'action', label: '', w: 132, cell: (a) => (a.on_deal ? <window.Pill hue="green" sz="sm">on this deal</window.Pill>
        : dealId && canWrite && window.AddBuyerToDealDrawer ? <window.Btn size="xs" variant="outline" onClick={() => setAdding(a)}>Add to deal</window.Btn> : null) },
    ],
  };
  const rowKey = (r, i) => (tab === 'nearby' ? `${r.source}-${r.id}` : tab === 'ours' ? r.engagement_id : tab === 'acquirers' ? r.buyer_id : r.id) || i;
  return (
    <window.FullScreenPanel active={api.active} title={name ? `${name} › Market` : 'Market'} maxWidth={1480} bodyScroll={false}
      tabs={MARKET_TABS.map(([k, label, l]) => ({ key: k, label, count: data ? (data[l] || []).length : null }))}
      tab={tab} onTab={(t) => api.set({ tab: t })}
      headerExtra={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {line || <span style={MARKET_DIM}>Median —</span>}
          <window.HelpTip text={compsMethod(st, kind === 'deal' ? "this deal's" : "the company's")} />
        </span>
      )}
      onClose={() => api.close({ via: 'button' })} onEscape={api.escape}>
      <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'auto' }}>
        {tab === 'nearby' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            {data && data.nearby_radius_mi != null && <window.Pill hue="slate" sz="sm">{data.nearby_radius_mi} mi</window.Pill>}
            {canWrite && cid && window.AtlasApi.compsNearbySweep && (
              <window.Btn size="xs" variant="outline" disabled={sweeping} onClick={sweep}>{sweeping ? 'Searching the web…' : 'Search the web'}</window.Btn>
            )}
          </div>
        )}
        {data === null
          ? <div style={{ padding: 12, color: 'var(--text-4)' }}>—</div>
          : <CompsTable cols={COLS[tab]} rows={rows} sort={sorts[tab]} onSort={toggleSort} rowKey={rowKey} minWidth={MW_MIN_WIDTH[tab]} />}
      </div>
      {api.active && adding && window.AddBuyerToDealDrawer && (
        <window.AddBuyerToDealDrawer deal={{ id: dealId, name: api.record.name }}
          initialBuyer={adding.company_id ? { id: adding.company_id, name: adding.firm } : undefined}
          toastAction={openBuyers} onClose={() => setAdding(null)} onDone={onAdded} />
      )}
    </window.FullScreenPanel>
  );
};

window.MarketCard = MarketCard;
window.MarketWorkspace = MarketWorkspace;
